import React from "react";
import Button, { ButtonType } from "../../assets/Button";
import { ITaskAttachment } from "../../models/taskManagement.model";

interface TaskManagerAttachmentProps {
  attachments: ITaskAttachment[];
  selectedFiles: FileList | null;
  inputId?: string;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUpload: () => Promise<void> | void;
  onRemoveAttachment: (attachmentId: string) => Promise<void> | void;
  showUpdateButton?: boolean;
  className?: string;
}

const TaskManagerAttachment: React.FC<TaskManagerAttachmentProps> = ({
  attachments,
  selectedFiles,
  inputId = "fileUploadInput",
  onFileSelect,
  onUpload,
  onRemoveAttachment,
  showUpdateButton = false,
  className = "",
}) => {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900">
          Attachments ({attachments?.length || 0})
        </h2>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => document.getElementById(inputId)?.click()}
            className="inline-flex items-center gap-2 px-2 py-1 text-xs font-medium bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 transition-all"
            aria-label="Upload attachment"
          >
            <svg
              className="w-4 h-4 text-indigo-600 group-hover:text-indigo-700 transition-transform transform group-hover:-translate-y-0.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 5 17 10" />
              <line x1="12" y1="5" x2="12" y2="17" />
            </svg>
            Upload
          </button>

          {showUpdateButton && selectedFiles && (
            <Button
              type={ButtonType.Extra}
              onClick={onUpload}
              className="text-xs !px-4 !py-1 !rounded"
            >
              Update
            </Button>
          )}

          <input
            id={inputId}
            type="file"
            multiple
            hidden
            onChange={onFileSelect}
          />
        </div>
      </div>
      {selectedFiles && selectedFiles.length > 0 && (
        <div className="bg-blue-50 rounded-lg p-2 border border-blue-200">
          {Array.from(selectedFiles).map((file, idx) => (
            <div
              key={`new-${idx}`}
              className="flex items-center gap-2 text-xs text-blue-700"
            >
              <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
              <span className="truncate">{file.name}</span>
              <span className="text-blue-500">
                ({(file.size / 1024 / 1024).toFixed(2)}MB)
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="p-4 space-y-3 max-h-52 overflow-y-auto">
        {attachments?.map((file, idx) => (
          <div
            key={file._id ?? idx}
            className="flex items-center justify-between p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
          >
            <div className="flex items-center gap-3">
              {file.isCrossVisible && (
                <button
                  className="text-red-500 hover:text-red-700"
                  onClick={() => onRemoveAttachment(file._id ?? "")}
                >
                  ✕
                </button>
              )}

              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.172 7l-6.586 6.586a2 2 0 002.828 2.828l6.586-6.586A4 4 0 0015.172 7z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2v2h-2z"
                />
              </svg>

              <span className="text-sm text-gray-800">{file.name}</span>
            </div>

            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-navyBlue-primary"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TaskManagerAttachment;
